/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

/**
 * Checkbox Card component. Used in new EDISON view.
 *
 * title - The title of the card.
 * subtitle - The subtitle of the card (optional).
 * inputName - The name of the form input (optional).
 * iconName - The icon to display right to input (optional).
 * maxQuantity - set the max quantity available.
 * value - The value of the input (optional).
 * price - The price to display (optional).
 * subPrice - The subPrice to display.
 * light - Turn on the light-mode (white background & disable icon) (default false).
 * disabled - Disable the component.
 * promoLabel - Show promo text.
 * isHorizontal - Turn on hozizontal mode (default false).
 * title-icon - The button right to the title (optional slot).
 * The content is in an unnamed slot.
 */
@customElement('nn-checkbox-card')
export class CheckboxCard extends LitElement {
  static styles = css`
    body {
      margin: 0;
    }
    .checkbox-card {
      align-items: flex-start;
      background: var(--nn-checkbox-card_checkbox-card_background, #f7f8fb);
      border: 1px solid
        var(--nn-checkbox-card_checkbox-card_border-color, #e8e8e8);
      border-radius: var(--nn-checkbox-card_checkbox-card_border-radius, 16px);
      cursor: pointer;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      font-family: 'font-medium';
      gap: var(--nn-checkbox-card_checkbox-card_gap, 12px);
      justify-content: start;
      padding: var(--nn-checkbox-card_checkbox-card_padding, 24px);
      text-align: left;
      transition: all ease-in-out
        var(--nn-checkbox-card_checkbox-card_transition, 0.3s);
      width: 100%;
    }
    .checkbox-card--light {
      background: var(--nn-checkbox-card_checkbox-card--light_background, #fff);
    }
    .checkbox-card--disabled {
      background: var(
        --nn-checkbox-card_checkbox-card--disabled_background,
        rgba(0, 0, 0, 0)
      );
      cursor: not-allowed;
      opacity: 0.5;
    }
    .checkbox-card:hover {
      border-color: var(
        --nn-checkbox-card_checkbox-card--hover_border-color,
        #bebebe
      );
    }
    .checkbox-card__container--left {
      display: flex;
      flex-basis: 60%;
      flex-direction: column;
      flex-wrap: nowrap;
      justify-content: space-between;
      order: 1;
    }
    .checkbox-card__container--left-header {
      display: flex;
      flex-basis: 60%;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
      padding-bottom: 10px;
    }
    .checkbox-card__container--right {
      align-items: flex-end;
      display: flex;
      flex-basis: 30%;
      flex-direction: column;
      flex-grow: 1;
      order: 2;
      text-align: right;
    }
    .checkbox-card__container--right > p {
      margin: var(
        --nn-checkbox-card_checkbox-card__container--right_p_margin,
        3px 0
      );
    }
    .checkbox-card__title_group {
      display: flex;
      flex-direction: column;
      flex-grow: 2;
      margin-bottom: var(
        --nn-checkbox-card_checkbox-card__title_group_margin-bottom,
        auto
      );
      margin-top: var(
        --nn-checkbox-card_checkbox-card__title_group_margin-top,
        auto
      );
    }
    .checkbox-card__title {
      display: flex;
      font-family: 'font-heavy';
      font-size: var(--nn-checkbox-card_checkbox-card__title_font-size, 18px);
      margin-bottom: var(
        --nn-checkbox-card_checkbox-card__title_margin-bottom,
        auto
      );
      margin-top: var(--nn-checkbox-card_checkbox-card__title_margin-top, auto);
    }
    .checkbox-card__subtitle {
      color: var(--nn-checkbox-card_checkbox-card__subtitle_color, #494949);
      font-family: 'font-light';
      font-size: var(
        --nn-checkbox-card_checkbox-card__subtitle_font-size,
        14px
      );
    }
    .checkbox-card__icon {
      margin-bottom: var(
        --nn-checkbox-card_checkbox-card__icon_margin-bottom,
        auto
      );
      margin-top: var(--nn-checkbox-card_checkbox-card__icon_margin-top, auto);
      padding: var(--nn-checkbox-card_checkbox-card__icon_padding, 0 10px);
      width: var(--nn-checkbox-card_checkbox-card__icon_width, 35px);
    }
    .checkbox-card--selected {
      background: var(
        --nn-checkbox-card_checkbox-card--selected_background,
        #f2f6fd
      );
      border: 1px solid
        var(--nn-checkbox-card_checkbox-card--selected_border-color, #437ce4);
    }
    .checkbox-card--selected:hover {
      border-color: var(
        --nn-checkbox-card_checkbox-card--selected--hover_border-color,
        #437ce4
      );
    }
    .checkbox-card__content {
      font-family: 'font-light';
      font-size: var(--nn-checkbox-card_checkbox-card__content_font-size, 14px);
    }
    .checkbox-card__checkbox {
      min-width: var(
        --nn-checkbox-card_checkbox-card__checkbox_min-width,
        18px
      );
    }
    .checkbox-card__select {
      background: url("data:image/svg+xml,%3Csvg width='9' height='5' viewBox='0 0 9 5' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.245923 1.0726C0.584213 0.824134 1.05518 0.804636 1.46873 1.0726L4.50092 3.16L7.53311 1.07259C7.94666 0.804636 8.4184 0.824134 8.75436 1.07259C9.09265 1.3205 9.07093 1.73943 8.75436 1.97229C8.43935 2.20515 5.11155 4.4803 5.11155 4.4803C5.0323 4.53927 4.93732 4.58617 4.83228 4.6182C4.72723 4.65024 4.61427 4.66675 4.50014 4.66675C4.38601 4.66675 4.27306 4.65024 4.16801 4.6182C4.06296 4.58617 3.96799 4.53927 3.88874 4.4803C3.88874 4.4803 0.562488 2.20515 0.245924 1.97229C-0.0714166 1.73943 -0.0923657 1.3205 0.245923 1.0726Z' fill='%23598BE7'/%3E%3C/svg%3E%0A")
        no-repeat 80%;
      border: 1px solid
        var(--nn-checkbox-card_checkbox-card__select_border-color, #598be7);
      border-radius: var(
        --nn-checkbox-card_checkbox-card__select_border-radius,
        3px
      );
      color: var(--nn-checkbox-card_checkbox-card__select_color, #598be7);
      height: var(--nn-checkbox-card_checkbox-card__select_height, 22px);
      margin-bottom: var(
        --nn-checkbox-card_checkbox-card__select_margin-bottom,
        auto
      );
      margin-top: var(
        --nn-checkbox-card_checkbox-card__select_margin-top,
        auto
      );
      padding: var(
        --nn-checkbox-card_checkbox-card__select_padding,
        2px 12px 2px 4px
      );
      width: var(--nn-checkbox-card_checkbox-card__select_width, 33px);
      -moz-appearance: none;
      -webkit-appearance: none;
      appearance: none;
    }
    .checkbox-card__question {
      cursor: pointer;
      margin: var(--nn-checkbox-card_checkbox-card__question_margin, 3px 5px);
    }
    .checkbox-card__price {
      flex: 3;
      font-family: 'font-heavy';
      font-size: var(--nn-checkbox-card_checkbox-card__price_font-size, 18px);
      margin: var(--nn-checkbox-card_checkbox-card__price_margin, 0);
      padding-left: var(
        --nn-checkbox-card_checkbox-card__price_padding-left,
        20px
      );
    }
    .checkbox-card__sub-price {
      font-family: 'font-light';
      font-size: var(
        --nn-checkbox-card_checkbox-card__sub-price_font-size,
        14px
      );
    }
    .checkbox-card--horizontal {
      flex-direction: column;
      flex-wrap: nowrap;
    }
    @media screen and (max-width: 768px) {
      .checkbox-card__container--left {
        flex-basis: 100%;
        order: 1;
      }
      .checkbox-card__container--right {
        align-items: flex-start;
        flex-basis: 80%;
        flex-direction: column;
        order: 2;
        text-align: left;
      }
      .checkbox-card__price {
        margin-bottom: var(
          --nn-checkbox-card_checkbox-card__price_margin-bottom,
          0
        );
      }
    }
  `;

  @property({ type: String }) title = 'Title';

  @property({ type: String }) subtitle = null;

  @property({ type: String }) inputName = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: String }) iconName = 'box-open-black-32';

  @property({ type: Number }) maxQuantity = 1;

  @property({ type: Number }) value = 0;

  @property({ type: String }) price = null;

  @property({ type: String }) subPrice = '';

  @property({ type: Boolean }) light = false;

  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) promoLabel = '';

  @property({ type: Boolean }) isHorizontal = false;

  renderInput() {
    if (this.maxQuantity > 1) {
      return html`
        <select
          class="checkbox-card__select"
          @blur=""
          @change="${this._dispatchOnChanged}"
          .id="${this.inputName}"
          ?disabled="${this.disabled}"
        >
          ${Array.from(Array(this.maxQuantity + 1).keys()).map(
            i => html`
              <option value=${i} ?selected=${i === this.value}>${i}</option>
            `
          )}
        </select>
      `;
    }

    return html`
      <input
        type="checkbox"
        .id="${this.inputName}"
        .name="${this.inputName}"
        class="checkbox-card__checkbox"
        .checked="${!!this.value}"
        @change="${this._dispatchOnChanged}"
        ?disabled="${this.disabled}"
      />
    `;
  }

  renderIcon() {
    if (!this.light && this.iconName !== '') {
      return html`
        <nn-generic-icon
          class="checkbox-card__icon"
          .iconName="${this.iconName}"
          ariaLabel="Icône du pack"
        ></nn-generic-icon>
      `;
    }
    return html`&nbsp;`;
  }

  render() {
    return html`
      <label
        class="checkbox-card${this.light ? ' checkbox-card--light' : ''}${this
          .value
          ? ' checkbox-card--selected'
          : ''}${this.disabled ? ' checkbox-card--disabled' : ''}${this
          .isHorizontal
          ? ' checkbox-card--horizontal'
          : ''}"
        for="${this.inputName}"
      >
        <div class="checkbox-card__container--left">
          <div class="checkbox-card__container--left-header">
            ${this.renderInput()} ${this.renderIcon()}
            <div class="checkbox-card__title_group">
              <p class="checkbox-card__title">
                ${this.title}
                <span
                  class="checkbox-card__question"
                  @click="${this._dispatchClick}"
                >
                  <slot name="title-icon"></slot>
                </span>
              </p>
              <span class="checkbox-card__subtitle">${this.subtitle}</span>
            </div>
          </div>
          <div>
            <slot class="checkbox-card__content"></slot>
          </div>
        </div>
        <div
          class="checkbox-card__container--right"
          style="${this.isHorizontal ? 'align-items: start' : ''}"
        >
          <slot name="price">
            <p class="checkbox-card__price">${this.price}</p>
          </slot>
          <p class="checkbox-card__sub-price">
            ${this.parseHtml(this.subPrice)}
          </p>
          <p>${this.parseHtml(this.promoLabel)}</p>
        </div>
      </label>
    `;
  }

  _dispatchOnChanged(e: Event) {
    const target = e.target as HTMLInputElement;
    let type = 'checkbox';
    if (this.maxQuantity > 1) {
      this.value = parseInt(target.value, 10);
      type = 'select';
    } else {
      this.value = target.checked ? 1 : 0;
    }
    const event = new CustomEvent('onChanged', {
      detail: {
        value: this.value,
        type,
      },
    });
    this.dispatchEvent(event);
  }

  _dispatchClick(e: Event) {
    e.preventDefault();
    const event = new CustomEvent('onClickInfo');
    this.dispatchEvent(event);
  }

  _dispatchSelectClick(e: Event) {
    e.stopPropagation();
    this._dispatchOnChanged(e);
  }

  // eslint-disable-next-line class-methods-use-this
  parseHtml(str?: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }
}
